/**
 * Page d'accueil du site (landing)
 *
 * Rôle : Sections hero, solution, problème, fonctionnalités, CTA ; scroll par section, bouton "Retour en haut".
 * Détecte si l'utilisateur est connecté (cookie rpm_auth) pour adapter le CTA. Affiche le Footer.
 * Technologies : React, Next.js (Link, Image, useRouter), Tailwind
 */
"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowRight, Users, Shield, Zap, Globe, TrendingUp, Briefcase, MapPin, Brain, Target, Truck, Calculator, ArrowUp, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import Footer from "./_components/Footer";

export default function Home() {
  const [isVisible, setIsVisible] = useState<{[key: string]: boolean}>({});
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const sectionRefs = useRef<{[key: string]: HTMLElement | null}>({});
  const router = useRouter();
  
  const sections = ['hero', 'solution', 'probleme', 'fonctionnalites', 'cta'];
  
  // Fonction pour naviguer vers la section suivante
  const scrollToNextSection = () => {
    const nextIndex = (currentSection + 1) % sections.length;
    const nextSection = sections[nextIndex];
    const element = sectionRefs.current[nextSection];
    
    if (element) {
      // Centrer seulement la section "Votre solution clé en main" (probleme)
      const blockValue = nextSection === 'probleme' ? 'center' : 'start';
      
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: blockValue
      });
    }
  };
  
  // Vérifier l'authentification côté client
  useEffect(() => {
    const checkAuth = () => {
      if (typeof window !== 'undefined') {
        const isLoggedIn = document.cookie.includes('rpm_auth=1');
        if (isLoggedIn) {
          router.push('/dashboard');
        }
      }
    };
    
    checkAuth();
  }, [router]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
            
            // Mettre à jour la section actuelle
            const sectionIndex = sections.indexOf(entry.target.id);
            if (sectionIndex !== -1) {
              setCurrentSection(sectionIndex);
            }
          }
        });
      },
      {
        threshold: 0.05,
        rootMargin: '200px 0px 0px 0px'
      }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [sections]);

  // Gestion du bouton "retour en haut"
  useEffect(() => {
    const handleScroll = () => {
      if (typeof window !== 'undefined') {
        setShowBackToTop(window.scrollY > 300);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const scrollToTop = () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const features = [
    {
      icon: <Users className="w-8 h-8" />,
      title: "CRM Spécialisé",
      description: "Gestion des relations, projets, réglementation."
    },
    {
      icon: <Briefcase className="w-8 h-8" />,
      title: "Catalogue Produits/Services",
      description: "Base équipements, services, prestations sectorisées."
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Gestion Commandes B2B",
      description: "Achats automatisés avec suivi complet."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Échanges Sécurisés",
      description: "Sécurité souveraine et conformité totale."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Connexions Automatisées",
      description: "API portuaire et synchronisation ERP."
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Onboarding 1 Heure",
      description: "Intégration rapide en une heure."
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      title: "Cartographie Interactive",
      description: "Cartographie partenaires et optimisation logistique."
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: "IA Contextuelle",
      description: "Analyse prédictive et optimisation marché."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Emplois Sectoriels",
      description: "Emploi sectoriel et matching compétences."
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Appels d'Offres Spécialisés",
      description: "Veille marchés maritimes, alertes, assistance, suivi."
    },
    {
      icon: <Truck className="w-8 h-8" />,
      title: "Transport Négocié",
      description: "Logistique transport optimisée à tarif réduit."
    },
    {
      icon: <Calculator className="w-8 h-8" />,
      title: "Fiscalité Digitale",
      description: "Automatisation fiscale maritime internationale."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header avec boutons de connexion */}
      <header className="fixed top-0 left-0 right-0 w-full bg-white border-b border-gray-200 z-50">
        <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex items-center gap-3 group/header-logo">
              <Image
                src="/logos/otulaa-rond.png"
                alt="Otulaa Logo"
                width={40}
                height={40}
                priority
                className="w-8 h-8 transition-transform duration-700 ease-out group-hover/header-logo:rotate-360 group-hover/header-logo:animate-spin"
              />
              <Image
                src="/logos/otulaa-texte.png"
                alt="Otulaa"
                width={80}
                height={20}
                priority
                className="h-auto"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              href="/marketplace"
              className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-blue-900 shadow-sm transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-md hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/10"
            >
              Espace particulier
            </Link>
            <Link
              href="/signin"
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-400 to-red-500 text-white px-4 py-2 text-sm font-bold shadow-sm transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-md active:scale-95"
            >
              Espace professionnel
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        id="hero"
        ref={(el) => { sectionRefs.current['hero'] = el; }}
        className={`relative bg-white py-8 rounded-3xl mx-6 mb-8 transition-all duration-1000 ease-out ${
          isVisible['hero'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center flex flex-col items-center justify-center min-h-[60vh]">
            <div className="mb-20 flex items-center justify-center gap-8 group/hero-logo">
              <Image
                src="/logos/otulaa-rond.png"
                alt="Otulaa Logo"
                width={120}
                height={120}
                priority
                className="w-24 h-24 md:w-32 md:h-32 transition-transform duration-700 ease-out group-hover/hero-logo:rotate-360 group-hover/hero-logo:animate-spin"
              />
              <Image
                src="/logos/otulaa-texte.png"
                alt="Otulaa"
                width={320}
                height={80}
                priority
                className="h-auto"
              />
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent mb-6 text-center leading-tight whitespace-nowrap">
              Tout votre business sur une plateforme
            </h1>
            <p className="text-2xl md:text-3xl mb-6 font-bold text-center whitespace-nowrap">
              <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
                Un logiciel de gestion agréé / Un site e-commerce / Une communauté
              </span>
            </p>
            <p className="text-lg md:text-xl text-blue-900 mb-12 font-bold text-center whitespace-nowrap">
              <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">Prise en main en une heure !</span> À partir de 100€ / mois HT (pour 5 utilisateurs, +20€ par utilisateur supplémentaire)
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
              <Link
                href="/signup"
                className="group relative inline-flex items-center gap-3 rounded-3xl bg-gradient-to-r from-orange-400 to-red-500 text-white px-12 py-5 text-xl font-bold shadow-2xl transition-all duration-700 ease-out hover:-translate-y-4 hover:shadow-3xl hover:scale-110 hover:rotate-2 active:scale-95 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-red-600 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                <div className="relative flex items-center gap-3">
                  <span>Rejoignez la communauté gratuitement</span>
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform duration-500" />
                </div>
              </Link>
              <Link
                href="/signup"
                className="group relative inline-flex items-center gap-2 rounded-3xl border-2 border-gray-300 bg-white px-10 py-5 text-lg font-bold text-blue-900 shadow-xl transition-all duration-700 ease-out hover:-translate-y-4 hover:shadow-2xl hover:scale-110 hover:-rotate-2 hover:border-orange-300 hover:bg-orange-50 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-400/5 to-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                <div className="relative">
                  <span className="group-hover:text-orange-600 transition-colors duration-500">Abonnez-vous</span>
                </div>
              </Link>
            </div>
            <p className="text-sm text-blue-900 mt-8">
              Aucune carte de crédit requise • Accès instantané
            </p>
          </div>
        </div>
      </section>

      {/* Apps & Solution Section */}
      <section 
        id="solution"
        ref={(el) => { sectionRefs.current['solution'] = el; }}
        className={`py-20 bg-white rounded-3xl mx-6 mb-8 transition-all duration-1000 ease-out ${
          isVisible['solution'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="mx-auto max-w-7xl px-6">
          <div className="group relative text-center mb-16 p-12 bg-gradient-to-br from-orange-50 via-orange-100 to-red-50 rounded-3xl shadow-2xl border-2 border-orange-200 hover:border-orange-400 hover:shadow-3xl transition-all duration-700 hover:-translate-y-2 hover:scale-105 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-400/5 to-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
            <div className="relative">
              <h2 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent mb-6">
                Imaginez votre boîte à outils digitale
              </h2>
              <p className="text-blue-900 text-xl mb-8 group-hover:text-blue-800 transition-colors duration-500">
                Vous avez quelque chose à améliorer ? Otulaa vous écoute, vous êtes au centre de notre proposition
              </p>
              <div className="flex flex-wrap justify-center gap-6 mb-8">
                <span className="bg-gradient-to-r from-orange-400 to-red-500 text-white px-8 py-4 rounded-full text-xl font-bold transition-all duration-500 ease-out hover:from-orange-500 hover:to-red-600 hover:scale-125 hover:shadow-2xl hover:rotate-3 cursor-pointer">
                  SIMPLE
                </span>
                <span className="bg-gradient-to-r from-orange-400 to-red-500 text-white px-8 py-4 rounded-full text-xl font-bold transition-all duration-500 ease-out hover:from-orange-500 hover:to-red-600 hover:scale-125 hover:shadow-2xl hover:-rotate-3 cursor-pointer">
                  SÉCURISÉ
                </span>
                <span className="bg-gradient-to-r from-orange-400 to-red-500 text-white px-8 py-4 rounded-full text-xl font-bold transition-all duration-500 ease-out hover:from-orange-500 hover:to-red-600 hover:scale-125 hover:shadow-2xl hover:rotate-3 cursor-pointer">
                  SPÉCIALISÉ
                </span>
              </div>
              <p className="text-2xl text-blue-900 font-bold mb-8 group-hover:text-orange-700 transition-colors duration-500">
                On ne vend pas un logiciel. On offre du business, du temps et une communauté professionnelle
              </p>
              <div className="opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0">
                <div className="w-24 h-1 bg-gradient-to-r from-orange-400 to-red-500 rounded-full mx-auto"></div>
              </div>
            </div>
          </div>

          {/* Apps Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6 mb-16">
            {[
              { name: "CRM Spécialisé", icon: "🚢" },
              { name: "Stock Partenaires", icon: "📦" },
              { name: "Procédures", icon: "📋" },
              { name: "Transport", icon: "🚛" },
              { name: "Calendrier", icon: "📅" },
              { name: "Échanges", icon: "💬" },
              { name: "Offres", icon: "🎯" },
              { name: "Projets", icon: "📊" },
              { name: "Fournisseurs", icon: "🤝" },
              { name: "Académie", icon: "🎓" },
              { name: "Listing", icon: "📋" },
              { name: "Support", icon: "🛠️" }
            ].map((app, index) => (
              <div 
                key={index} 
                className={`group relative text-center p-6 bg-white rounded-3xl shadow-xl border-2 border-gray-200 hover:border-orange-300 hover:shadow-2xl transition-all duration-700 hover:-translate-y-3 hover:scale-110 hover:rotate-2 overflow-hidden ${
                  isVisible['solution'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-orange-400/5 to-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                <div className="relative">
                  <div className="text-5xl mb-4 group-hover:scale-125 group-hover:rotate-12 transition-all duration-700">{app.icon}</div>
                  <h3 className="text-sm font-bold text-blue-900 group-hover:text-orange-600 transition-colors duration-500">{app.name}</h3>
                  <div className="mt-4 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-2 group-hover:translate-y-0">
                    <div className="w-8 h-0.5 bg-gradient-to-r from-orange-400 to-red-500 rounded-full mx-auto"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Votre solution clé en main Section */}
      <section 
        id="probleme"
        ref={(el) => { sectionRefs.current['probleme'] = el; }}
        className={`py-12 bg-white rounded-3xl mx-6 mb-8 transition-all duration-1000 ease-out ${
          isVisible['probleme'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent mb-6">
              Votre solution clé en main
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-orange-400 to-red-500 rounded-full mx-auto mb-8"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="group relative bg-white rounded-3xl p-10 shadow-2xl border-2 border-gray-200 hover:border-orange-300 hover:shadow-3xl transition-all duration-700 hover:-translate-y-4 hover:scale-110 hover:rotate-1 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-400/5 to-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              <div className="relative text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-400 via-orange-500 to-red-500 rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-125 group-hover:rotate-12 transition-all duration-700 shadow-lg">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <h3 className="text-2xl font-extrabold text-blue-900 mb-4 group-hover:text-orange-600 transition-colors duration-500 text-center h-16 flex items-center justify-center">
                  <span className="text-green-600 mr-2 text-4xl font-bold">+</span> De business
                </h3>
                <p className="text-blue-900 text-lg font-semibold leading-relaxed group-hover:text-blue-800 transition-colors duration-500 text-center h-12 flex items-center justify-center">
                  Site web, marketplace et outils intégrés
                </p>
                <div className="mt-6 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0">
                  <div className="w-12 h-1 bg-gradient-to-r from-orange-400 to-red-500 rounded-full mx-auto"></div>
                </div>
              </div>
            </div>
            
            <div className="group relative bg-white rounded-3xl p-10 shadow-2xl border-2 border-gray-200 hover:border-orange-300 hover:shadow-3xl transition-all duration-700 hover:-translate-y-4 hover:scale-110 hover:-rotate-1 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-400/5 to-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              <div className="relative text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-400 via-orange-500 to-red-500 rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-125 group-hover:-rotate-12 transition-all duration-700 shadow-lg">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-extrabold text-blue-900 mb-4 group-hover:text-orange-600 transition-colors duration-500 text-center h-16 flex items-center justify-center">
                  <span className="text-red-600 mr-2 text-4xl font-bold">-</span> De temps administratif
                </h3>
                <p className="text-blue-900 text-lg font-semibold leading-relaxed group-hover:text-blue-800 transition-colors duration-500 text-center h-12 flex items-center justify-center">
                  Automatisation, process et simplification
                </p>
                <div className="mt-6 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0">
                  <div className="w-12 h-1 bg-gradient-to-r from-orange-400 to-red-500 rounded-full mx-auto"></div>
                </div>
              </div>
            </div>
            
            <div className="group relative bg-white rounded-3xl p-10 shadow-2xl border-2 border-gray-200 hover:border-orange-300 hover:shadow-3xl transition-all duration-700 hover:-translate-y-4 hover:scale-110 hover:rotate-1 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-400/5 to-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              <div className="relative text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-400 via-orange-500 to-red-500 rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-125 group-hover:rotate-12 transition-all duration-700 shadow-lg">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-extrabold text-blue-900 mb-4 group-hover:text-orange-600 transition-colors duration-500 text-center h-16 flex items-center justify-center">
                  <span className="text-green-600 mr-2 text-4xl font-bold">+</span> De réseau professionnel
                </h3>
                <p className="text-blue-900 text-lg font-semibold leading-relaxed group-hover:text-blue-800 transition-colors duration-500 text-center h-12 flex items-center justify-center">
                  Forum, donneurs d&apos;ordre et appels d&apos;offres
                </p>
                <div className="mt-6 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0">
                  <div className="w-12 h-1 bg-gradient-to-r from-orange-400 to-red-500 rounded-full mx-auto"></div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-blue-900 italic">
              « Faites simple. Aussi simple que possible, mais pas plus. »
            </p>
            <p className="text-sm text-blue-900 mt-2">— Albert Einstein</p>
          </div>
        </div>
      </section>

      {/* Fonctionnalités détaillées Section */}
      <section 
        id="fonctionnalites"
        ref={(el) => { sectionRefs.current['fonctionnalites'] = el; }}
        className={`py-20 bg-white rounded-3xl mx-6 mb-8 transition-all duration-500 ease-out ${
          isVisible['fonctionnalites'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className={`group relative bg-white rounded-3xl p-8 shadow-xl border-2 border-gray-200 hover:border-orange-300 hover:shadow-2xl transition-all duration-500 hover:-translate-y-4 hover:scale-110 hover:rotate-1 overflow-hidden ${
                  isVisible['fonctionnalites'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 30}ms` }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-orange-400/5 to-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                <div className="relative">
                  <div className="text-orange-500 mb-6 group-hover:scale-125 group-hover:rotate-12 transition-all duration-700">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-extrabold text-blue-900 mb-4 group-hover:text-orange-600 transition-colors duration-500">{feature.title}</h3>
                  <p className="text-blue-900 text-lg font-semibold leading-relaxed group-hover:text-blue-800 transition-colors duration-500">{feature.description}</p>
                  <div className="mt-6 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0">
                    <div className="w-12 h-1 bg-gradient-to-r from-orange-400 to-red-500 rounded-full"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        id="cta"
        ref={(el) => { sectionRefs.current['cta'] = el; }}
        className={`py-20 bg-gradient-to-r from-orange-400 to-red-500 rounded-3xl mx-6 mb-8 transition-all duration-1000 ease-out ${
          isVisible['cta'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à révolutionner votre business en 1 heure ?
          </h2>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link
              href="/signup"
              className="group relative inline-flex items-center gap-3 rounded-3xl bg-white text-orange-600 px-12 py-5 text-xl font-bold shadow-2xl transition-all duration-700 ease-out hover:-translate-y-4 hover:shadow-3xl hover:scale-110 hover:rotate-2 active:scale-95 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-orange-50 to-red-50 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              <div className="relative flex items-center gap-3">
                <span>Commencer l&apos;aventure</span>
                <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform duration-500" />
              </div>
            </Link>
            <Link
              href="/signin"
              className="group relative inline-flex items-center gap-2 rounded-3xl border-2 border-white text-white px-10 py-5 text-lg font-bold shadow-xl transition-all duration-700 ease-out hover:-translate-y-4 hover:shadow-2xl hover:scale-110 hover:-rotate-2 hover:bg-white hover:text-orange-600 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              <div className="relative">
                <span>Se connecter</span>
              </div>
            </Link>
          </div>
        </div>
      </section>


      {/* Bouton navigation entre sections */}
      <button
        onClick={scrollToNextSection}
        className="fixed bottom-6 left-6 z-40 w-14 h-14 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 ease-out hover:scale-110 active:scale-95 flex items-center justify-center group"
        title={`Aller à la section suivante (${currentSection + 1}/${sections.length})`}
      >
        <ChevronDown className="w-6 h-6 group-hover:translate-y-1 transition-transform duration-300" />
      </button>

      {/* Bouton retour en haut */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 bg-gradient-to-r from-orange-400 to-red-500 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 ease-out hover:scale-110 active:scale-95 flex items-center justify-center group"
          aria-label="Retour en haut"
        >
          <ArrowUp className="w-6 h-6 transition-transform group-hover:scale-110" />
        </button>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
}
